import React from 'react';
import { FormControl, Select, MenuItem, Button, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  FilterLoading,
  FilterValue,
  filterInputSx,
  filterMenuProps,
  onlyButtonSx,
  optionItemSx,
  selectAllItemSx,
  summarizeSelection,
} from './ui/filterStyles';
import { useFilterContext } from '../context/FilterContext';

interface PillarsFilterProps {
  orgId: number;
  selectedPillars: string[];
  onPillarsChange: (pillars: string[]) => void;
}

export const PillarsFilter: React.FC<PillarsFilterProps> = ({
  orgId,
  selectedPillars,
  onPillarsChange,
}) => {
  const { availablePillars, isInitialized } = useFilterContext();

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    // Ensure we're always working with an array
    let newSelectedValue: string[];
    
    // Handle the case where the value might be a string or an array
    if (typeof event.target.value === 'string') {
      newSelectedValue = event.target.value.split(',');
    } else {
      newSelectedValue = event.target.value;
    }
    
    // Prevent deselection if only one pillar is currently selected and the new selection is empty
    if (selectedPillars.length === 1 && newSelectedValue.length === 0) {
      // Optionally provide user feedback here (e.g., a snackbar)
      return; // Do not call onPillarsChange
    }
    
    onPillarsChange(newSelectedValue);
  };

  const handleOnly = (pillar: string) => {
    onPillarsChange([pillar]);
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onPillarsChange([...availablePillars]);
  };

  const pillarOptions = availablePillars.map(p => ({ id: p, name: p }));

  if (!isInitialized) {
    return <FilterLoading label="pillars" />;
  }

  return (
    <FormControl fullWidth>
      <Select
        id="pillars-filter"
        multiple
        displayEmpty
        value={selectedPillars}
        onChange={handleChange}
        input={<OutlinedInput sx={filterInputSx} />}
        renderValue={(selected) => (
          <FilterValue
            label="Pillars"
            value={summarizeSelection(Array.isArray(selected) ? selected : [], pillarOptions)}
          />
        )}
        MenuProps={filterMenuProps}
      >
        <MenuItem onMouseDown={handleSelectAll} sx={selectAllItemSx}>
          Select all
        </MenuItem>

        {availablePillars.map(pillar => (
          <MenuItem
            key={pillar}
            value={pillar}
            sx={optionItemSx}
          >
            <Checkbox size="small" checked={selectedPillars.indexOf(pillar) > -1} sx={{ p: 0, mr: 1 }} />
            <ListItemText primary={pillar} primaryTypographyProps={{ fontSize: '0.875rem' }} />
            <Button
              className="filter-only"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOnly(pillar);
              }}
              sx={onlyButtonSx}
            >
              Only
            </Button>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
